/**
 * @file section-meta.ts
 * @description Справочники редактора секции v2 (phase19.16 Р2;
 * uiux-design-r2 §2, §4; SB100): названия видов элементов со значками и
 * подсказками, роли фона, значки кнопок, пропорции колонок (быстрые схемы
 * долей v3). Ступени отступов «как у сайта» в px — `look-space.ts` (SB120).
 *
 * Реестры (OCP): новый вид элемента в shared — новая запись здесь;
 * `Record<SectionElementKind, …>` не даст собрать кабинет, если вид забыли
 * (меню «⊕ элемент», строка элемента, панель — все читают этот реестр).
 */

import type {
  SectionBgRole,
  SectionElement,
  SectionElementKind,
  SectionProductBadge,
  SectionRatio,
  SiteIconKey,
} from "@chaiberi/shared";
import {
  AlignLeft,
  ArrowRight,
  AudioLines,
  BookOpen,
  Contact,
  Grid2x2,
  Hash,
  Heading,
  Image as ImageIcon,
  Images,
  LayoutGrid,
  Mail,
  Megaphone,
  MessageSquare,
  MessageCircleQuestion,
  MousePointerClick,
  Phone,
  Send,
  ShoppingBag,
  ShoppingCart,
  Star,
  Table,
  Tag,
  Video,
  type LucideIcon,
} from "lucide-react";

import { PRODUCT_ELEMENT_META } from "./product-element-meta";

export interface ElementKindMeta {
  /** Название в меню и строке: «Кнопка». */
  label: string;
  /** «Для чего» — строка под названием в меню «⊕ элемент». */
  hint: string;
  icon: LucideIcon;
}

export const ELEMENT_KIND_META: Readonly<Record<SectionElementKind, ElementKindMeta>> = {
  heading: { label: "Заголовок", hint: "Главный, раздела или подраздела", icon: Heading },
  eyebrow: { label: "Надпись", hint: "Короткая строка над заголовком", icon: Tag },
  text: { label: "Текст", hint: "Абзацы — через пустую строку", icon: AlignLeft },
  image: { label: "Фото", hint: "Картинка из медиатеки", icon: ImageIcon },
  gallery: { label: "Галерея", hint: "Несколько фото рядом", icon: Images },
  video: { label: "Видео", hint: "Файл из медиатеки", icon: Video },
  audio: { label: "Аудио", hint: "Звук из медиатеки", icon: AudioLines },
  button: { label: "Кнопка", hint: "Ведёт на страницу, в каталог или мессенджер", icon: MousePointerClick },
  tiles: { label: "Плитки-ссылки", hint: "Фото с подписью — ведут куда нужно", icon: LayoutGrid },
  products: { label: "Товары", hint: "Новинки, с бейджем или выбранные", icon: ShoppingBag },
  categories: { label: "Категории", hint: "Разделы каталога плитками", icon: Grid2x2 },
  articles: { label: "Статьи", hint: "Свежие или выбранные статьи блога", icon: BookOpen },
  reviews: { label: "Отзывы", hint: "Отзывы покупателей — из базы отзывов", icon: Star },
  faq: { label: "Вопросы и ответы", hint: "Ответ раскрывается по нажатию", icon: MessageCircleQuestion },
  contacts: { label: "Контакты", hint: "Из «Настроек сайта»", icon: Contact },
  newsletter: { label: "Подписка", hint: "Поле «Подписаться на новости»", icon: Mail },
  announcements: { label: "Объявления", hint: "Короткие сообщения, которые сменяют друг друга", icon: Megaphone },
  stats: { label: "Цифры", hint: "Крупные числа с подписями: «15 лет», «2000 заказов»", icon: Hash },
  table: { label: "Таблица", hint: "Столбцы и строки: размеры, цены, сроки", icon: Table },
  // Р2а: элементы данных товара — только в шаблоне страницы товара.
  ...PRODUCT_ELEMENT_META,
};

/** Короткое описание элемента для строки сетки: «Кнопка: Выбрать чай». */
export function elementSummary(element: SectionElement): string {
  const label = ELEMENT_KIND_META[element.kind].label;
  const text = elementSnippet(element).replace(/\s+/g, " ").trim();
  return text ? `${label}: ${text}` : label;
}

function elementSnippet(element: SectionElement): string {
  switch (element.kind) {
    case "heading":
    case "eyebrow":
    case "text":
      return element.text;
    case "button":
      return element.label;
    case "image":
      return element.mediaId ? (element.alt ?? "") : "место под фото";
    case "gallery":
      return `${element.mediaIds.length} фото`;
    case "tiles":
      return `${element.items.length} плиток`;
    case "faq":
      return `${element.items.length} вопросов`;
    case "announcements":
      return element.source === "promotions" ? "из «Акций»" : (element.items[0]?.text ?? "");
    case "stats":
      return element.items.map((i) => i.value).join(" · ");
    case "table":
      return `${element.columns.length} × ${element.rows.length}`;
    default:
      return "";
  }
}

/** Роли фона секции словами владельца (uiux-design-r2 §2). */
export const BG_ROLE_LABELS: Readonly<Record<SectionBgRole, string>> = {
  pageBg: "Как страница",
  cardBg: "Карточка",
  panelBg: "Плашка",
  darkBg: "Тёмный",
  accent: "Акцент",
};

export interface IconMeta {
  label: string;
  icon: LucideIcon;
}

/** Значки кнопок и шапки раздела. */
export const SITE_ICON_META: Readonly<Record<SiteIconKey, IconMeta>> = {
  arrow: { label: "Стрелка", icon: ArrowRight },
  telegram: { label: "Telegram", icon: Send },
  vk: { label: "VK", icon: MessageSquare },
  phone: { label: "Телефон", icon: Phone },
  cart: { label: "Корзина", icon: ShoppingCart },
  mail: { label: "Письмо", icon: Mail },
};

/** Подпись пропорции колонок: «1:1», «2:1». */
export function ratioLabel(ratio: SectionRatio): string {
  return ratio === "1" ? "одна" : ratio;
}

/** Доли колонок для мини-схемы («2:1» → [2, 1]). */
export function ratioParts(ratio: SectionRatio): number[] {
  return ratio.split(":").map(Number);
}

/** «1 колонка», «2 колонки». */
export function columnsLabel(count: number): string {
  return count === 1 ? "1 колонка" : `${count} колонки`;
}

/** Подписи ручных бейджей — как в карточке товара (SB32). */
export const PRODUCT_BADGE_LABELS: Readonly<Record<SectionProductBadge, string>> = {
  BESTSELLER: "Бестселлер",
  COMING_SOON: "Скоро в продаже",
  SALE: "Распродажа",
};

/** Точки 3×3 по строкам: 1 — левый верхний угол, 9 — правый нижний. */
export const ANCHOR_NAMES = [
  "слева сверху",
  "сверху по центру",
  "справа сверху",
  "слева по центру",
  "в центре",
  "справа по центру",
  "слева снизу",
  "снизу по центру",
  "справа снизу",
] as const;

/**
 * Кнопка подвала панели блока «Сохранить блок · Отменить правки»: на узком
 * экране и в узкой панели (контейнер `@container` панели < 384, на 768 —
 * 296 px) кнопки делят строку поровну и не вылезают за панель (QA 3б A3).
 */
export const SECTION_FOOT_BUTTON_CLASS = "min-w-0 max-sm:h-12 max-sm:flex-1 max-sm:px-2 @max-sm:flex-1 @max-sm:px-2";

/** «Сколько» у элементов с выборкой (категории, статьи, отзывы): ступени до предела. */
export function countOptions(max: number): Array<{ value: string; label: string }> {
  return [2, 3, 4, 6, 8, 12].filter((n) => n <= max).map((n) => ({ value: String(n), label: String(n) }));
}

/** Подпись колонки не длиннее 28 знаков (uiux-sb120-stage1 §2.6). */
export const COLUMN_CAPTION_MAX = 28;

/**
 * Подпись колонки по первому элементу — для порядка на телефоне:
 * «Фото», «Заголовок «Иван-чай…»», пустая колонка — «пусто». Обрезается
 * после 28 знаков с «…».
 */
export function columnCaption(column: { elements: readonly SectionElement[] }): string {
  const first = column.elements[0];
  if (!first) return "пусто";
  const label = ELEMENT_KIND_META[first.kind].label;
  const text = elementSnippet(first).replace(/\s+/g, " ").trim();
  const full = text ? `${label} «${text}»` : label;
  if (full.length <= COLUMN_CAPTION_MAX) return full;
  // «…» и закрывающая кавычка входят в 28 знаков.
  const tail = text ? "…»" : "…";
  return `${full.slice(0, COLUMN_CAPTION_MAX - tail.length).trimEnd()}${tail}`;
}
